/**
 * SessionsAddEditPageV2 — redesigned edit-page chrome, port of the
 * prototype's `.screen-edit` (cms-redesign-mockup.html ~8161+).
 *
 * Layout:
 *   • Sticky `edit-header` at top:
 *       - Eyebrow ('Editing session' / 'New session')
 *       - H1 with session_name (fetched lazily; '#42' fallback)
 *       - JetBrains-Mono sub-line: session_id + last-saved
 *       - Action cluster: Discard / Duplicate (edit-only) / Save
 *   • Form body: <AddEditSessionV2> renders each section as a
 *     FormSection card built on the new edit primitives
 *     (FormSection / FieldBlock / TextInput / ToggleSwitch / etc.).
 *
 * Save flow: the V2 Save button submits the form via the standard
 * `form="session-form" type="submit"` pairing — the form's onSubmit
 * lives in AddEditSessionV2 and runs the same v1 addEditSession
 * action with the same payload shape.
 */
import React, { useCallback, useState } from 'react';
import { useLocation, useParams, Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import AddEditSessionV2 from '../../AddEditSession/v2/AddEditSessionV2';
import { duplicateSession } from '../../../actions/sessionActions';
// PageHeader isn't used here — the edit page needs a sticky action
// row that the standard PageHeader doesn't model. Sibling V2 pages
// (list views) use PageHeader; the edit pages get their own
// `edit-header` chrome inline below.
import { Button, cn } from '../../redesign';

/* ─── Icons ────────────────────────────────────────────────────────── */

const SaveIconSvg = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
    <polyline points="17 21 17 13 7 13 7 21" />
    <polyline points="7 3 7 8 15 8" />
  </svg>
);

const CopyIconSvg = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

/* ─── Main ─────────────────────────────────────────────────────────── */

export default function SessionsAddEditPageV2() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const isEdit = location.pathname.includes('edit');
  const [isDuplicating, setIsDuplicating] = useState(false);

  // The form fires the only GET /sessions/{id} call and hands back
  // the title + updated_at via `onLoaded`. Memoised so the form's
  // load effect doesn't re-fire on every re-render here.
  const [sessionName, setSessionName] = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const handleFormLoaded = useCallback(({ session_name, session_updated_at }) => {
    setSessionName(session_name || null);
    setLastSavedAt(session_updated_at || null);
  }, []);

  const handleDuplicate = async () => {
    const { isConfirmed } = await Swal.fire({
      title: 'Duplicate Session?',
      text: 'This will create a copy of this session (inactive by default).',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Duplicate',
    });
    if (!isConfirmed) return;

    try {
      setIsDuplicating(true);
      const newSession = await dispatch(duplicateSession(id));
      Swal.fire({
        icon: 'success',
        title: 'Session Duplicated!',
        text: `Created: ${newSession.session_name}`,
        timer: 1500,
      }).then(() => {
        navigate(`/sessions/edit/${newSession.session_id}`);
      });
    } catch (err) {
      console.error('Error duplicating session:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to duplicate session. Please try again.',
      });
    } finally {
      setIsDuplicating(false);
    }
  };

  // Format "last saved" relative-ish string. The endpoint returns an
  // ISO timestamp; we render it as a short human label so the sub-
  // line stays compact under the title.
  const lastSavedLabel = lastSavedAt
    ? `last saved ${new Date(lastSavedAt).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })}`
    : null;

  const eyebrow = isEdit ? 'Editing session' : 'New session';
  // Title: actual session_name when we have it; otherwise a bare
  // `Session #N` placeholder for edit mode or 'Untitled session' on
  // create. The `Untitled` placeholder mirrors how the list cells
  // handle missing names elsewhere in the V2 surface.
  const title = isEdit
    ? (sessionName || `Session #${id}`)
    : 'Untitled session';

  return (
    <div className="bg-bg min-h-full">
      {/* `.edit-header` — sticky, sits above the form canvas, carries
       *  the eyebrow / title / id line on the left and the action
       *  cluster on the right. */}
      <div
        className="sticky top-0 bg-bg border-b border-sand"
        style={{ zIndex: 20 }}
      >
        <div
          className="flex items-start"
          style={{ padding: '20px 28px', gap: 16, flexWrap: 'wrap' }}
        >
          <div className="flex-1 min-w-0">
            <div
              className="font-condensed font-bold uppercase text-text-muted"
              style={{ fontSize: 11, letterSpacing: '0.08em' }}
            >
              {eyebrow}
            </div>
            <h1
              className={cn(
                'text-navy truncate',
                !sessionName && isEdit && 'text-text-light italic',
              )}
              style={{
                fontSize: 28,
                fontWeight: 700,
                margin: '4px 0 4px',
                letterSpacing: '-0.015em',
                lineHeight: 1.15,
              }}
              title={title}
            >
              {title}
            </h1>
            <div
              className="font-mono text-text-muted"
              style={{ fontSize: 12 }}
            >
              {isEdit ? (
                <>
                  session_id · <strong className="text-navy">{id}</strong>
                  {lastSavedLabel && (
                    <>
                      {' · '}
                      <span>{lastSavedLabel}</span>
                    </>
                  )}
                </>
              ) : (
                <span>Fill the fields below and click Save to create.</span>
              )}
            </div>
          </div>

          <div className="flex items-center" style={{ gap: 8 }}>
            <Link to="/sessions" className="no-underline">
              <Button variant="ghost">Discard Changes</Button>
            </Link>
            {isEdit && (
              <Button
                variant="default"
                onClick={handleDuplicate}
                disabled={isDuplicating}
                leadingIcon={CopyIconSvg}
              >
                {isDuplicating ? 'Duplicating…' : 'Duplicate'}
              </Button>
            )}
            <Button
              variant="coral"
              type="submit"
              form="session-form"
              leadingIcon={SaveIconSvg}
            >
              {isEdit ? 'Save' : 'Create'}
            </Button>
          </div>
        </div>
      </div>

      {/* Form canvas — AddEditSessionV2 renders the `.edit-canvas`
       *  two-column grid internally (main form + right rail). The
       *  outer padding here matches the prototype's
       *  `padding: 24px 32px 60px` on `.edit-canvas` at the 1300px
       *  breakpoint. */}
      <div style={{ padding: '24px 32px 60px' }}>
        <AddEditSessionV2 isEdit={isEdit} selectedId={id} onLoaded={handleFormLoaded} />
      </div>
    </div>
  );
}

